// images references in the manifest
import "./assets/icon-32.png";
import "./assets/icon-64.png";
import "@zebrabi/fields-pane/components-registration";

import { analyticsHandler, ignoredEditEvents } from "@zebrabi/analytics-tracking/AnaliticsHandler";
import { ActionType } from "@zebrabi/office-licensing/ActionType";
import { OwnerUserInfo, CurrentUserInfo } from "@zebrabi/office-licensing/components/UserInfo";
import ActionObserver from "@zebrabi/office-licensing/observers/ActionObserver";
import { licensing } from "@zebrabi/office-licensing/Licensing";
import * as process from "process";
import { DataHelper } from "./dataHelper";
import { ViewModel, Viewport } from "./interfaces";
import { CHART_SETTINGS_NAME, ChartSettings } from "./settings/chartSettings";
import * as viewModelFactory from "./viewModel/viewModelFactory";
import * as viewModelHelpers from "./viewModel/viewModelHelperFunctions";
import { delay } from "./helpers";

import { valueFormatter } from "powerbi-visuals-utils-formattingutils";
import * as d3 from "./d3";
import {
    BLOCK,
    COMMENT_BOX_RESIZE_LINE,
    DISPLAY,
    NONE,
    ZEBRABI_CHART_SVG_CONTAINER,
    CLICK,
    DEFAULT_FONT_SIZE,
    FONT_SIZE_UNIT,
    MOUSELEAVE,
    MOUSEENTER,
    DIV,
} from "./library/constants";
import { CONTEXT_MENU, CONTEXT_MENU_DIV } from "@zebrabi/constants";
import { INFO_BOX_CONTAINER } from "./consts";
import InfoBoxHandler from "./ui/InfoBoxHandler";
import {
    subscribeToXSpreadSheetData,
    subscribeToTableDataEditorData,
    initTableDataEditorStore,
    VisualType,
} from "@zebrabi/table-data-editor";
import { DataView, getOfficeStorageTableData, setOfficeStorageTableData, prepareTableData } from "@zebrabi/table-data";
import { initChartChooserStore, mountChartChooser } from "@zebrabi/chart-chooser";
import GlobalToolbar from "@zebrabi/global-toolbar/dist/lib/components/GlobalToolbar";
import GlobalToolbarOld from "@zebrabi/global-toolbar-old/components/GlobalToolbar";
import AboutSwitcher from "./toolbar/components/AboutSwitcher";
import DataTableEditorSwitcher from "./toolbar/components/DataTableEditorSwitcher";
import TableDataEditorObserver from "./toolbar/observers/TableDataEditorObserver";
import StackedChartSwitcher from "./toolbar/components/StackedChartSwitcher";
import StackedChartObserver from "./toolbar/observers/StackedChartObserver";
import AccountSwitcher from "./toolbar/components/AccountSwitcher";
import { openURL } from "@zebrabi/office-licensing/helpers";
import { signInService } from "@zebrabi/office-licensing/components/signin/MSFTSignIn";
import { extractExistingAnalyticsId, initializeAnalyticsOptOut, initializeAnalyticsReporting } from "@zebrabi/analytics-tracking/helpers";
import axios from "axios";
import { flagHandler } from "@zebrabi/zebrabi-core";
import { FIELDS_LIST_FLAG } from "@zebrabi/office-types";
import { sortAscending, sortDescending } from "./helpers";
import {
    OrganizationStyleData,
    OrganizationStyleSettings,
    getAvailableOrganizationStyles,
    getOrganizationStyleById,
} from "@zebrabi/data-helpers/organizationStyles";
import ChartSettingsSwitcher from "./toolbar/components/ChartSettingsSwitcher";
import ChartSettingsSwitcherObserver from "./toolbar/observers/ChartSettingsSwitcherObserver";
import AboutSwitcherOld from "./toolbar/components/AboutSwitcherOld";
import FieldsChartsSwitcher from "./toolbar/components/FieldsChartsSwitcher";
import FieldsChartsSwitcherObserver from "./toolbar/observers/FieldsChartsSwitcherObserver";
import SettingsChartsSwitcher from "./toolbar/components/SettingsChartsSwitcher";
import SettingsChartsSwitcherObserver from "./toolbar/observers/SettingsChartsSwitcherObserver";

import {
    Charts,
    ChartsViewMode,
    ChartType,
    createChartsInstance,
    getQuillOpAttributes,
    ShowTopNChartsOptions,
    SortDirection as Sort,
} from "@zebrabi/charts";
import { debugLogger, type VerbosityBitString } from "@zebrabi/debug-logger";
import {
    AnnotationAction,
    AnnotationAdapterSource,
    AnnotationsService,
    createAnnotationModeContextProvider,
    store as annotationLayerStore,
    VisualViewMode,
    type AnnotationLayerStoreSubscriber,
    type AnyAnnotationsAdapter,
} from "@zebrabi/annotation-layer";
import { VisualInstanceAnnotationsAdapter } from "./AnnotationLayerAdapter/VisualInstanceAnnotationsAdapter";
import { canAddCagrArrow, prepareAnnotationLayer, type EnabledAnnotationActions } from "./AnnotationLayerAdapter/annotation-layer-helpers";
import { createAnnotationLayerStoreSubscriber } from "./AnnotationLayerAdapter/annotationLayerStoreSubscriber";
import AnnotationModeSwitcher from "./toolbar/components/AnnotationModeSwitcher";
import AnnotationModeObserver from "./toolbar/observers/AnnotationModeObserver";
import { SelectionService } from "@zebrabi/selection-service";
import initializeInteractionHandlers from "./initializeInteractionHandlers";
import { OfficeSelectionService } from "./SelectionService";

// eslint-disable-next-line import-x/no-relative-packages -- `npm run flags:dev` writes flagsmith.json at the repo root
import fsm from "../../../flagsmith.json";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { convertLegacyTitleToDelta } from "@zebrabi/office-settings/legacyVisualTitle";
import { SHOW_CHOOSER_LINK_ID } from "@zebrabi/data-helpers/editData";

import { initExcelDataLinkingStore } from "@zebrabi/excel-data-linking/mount";
import { ExcelDataLinkingStore } from "@zebrabi/excel-data-linking";
import { transformSharePointResponse } from "@zebrabi/excel-data-linking/helpers";
import { dataLinkingEventBus } from "@zebrabi/excel-data-linking/DataLinkingEventBus";
import SharepointBrowserAPI from "@zebrabi/sharepoint-api-browser/SharepointBrowserAPI";
import IconButton from "@zebrabi/design-library/dist/lib/atoms/IconButton/IconButton";
import { IconName, MessageBarService } from "@zebrabi/ui-components";

// design-system tokens (--zbi-* custom properties) consumed by the zbi-* web components; zen loads this in its app shell
import "@zebrabi/ui-components/themes/variables.css";
import "./visual.less";
import "./style/ui.less";

// Initialization
Office.onReady(async (info) => {
    if (process.env.DEBUG_LOG === "true") {
        console.debug("Office ready info: ", info);
        // charts swallows plot errors into debugLogger.debug (see ZEN-3323 try/catch) — surface them in dev
        debugLogger.setVerbosity("ERROR | WARNING | LOG | DEBUG | INFO" as VerbosityBitString);
    }

    const isEdgeOrIE = window.navigator.userAgent.indexOf("Trident") !== -1 || window.navigator.userAgent.indexOf("Edge") !== -1;
    if (isEdgeOrIE) {
        const loadingElementMsg = document.getElementById("main-message");
        if (loadingElementMsg) {
            loadingElementMsg.textContent =
                "This Office environment is not supported. Please upgrade your Microsoft Office to newer version.";
        }
        return;
    }

    if (info.host === Office.HostType.Excel || info.host === Office.HostType.PowerPoint) {
        const settings: ChartSettings = getOfficeSettings(CHART_SETTINGS_NAME);

        // settings are null on the chart chooser screen, where there is no title to migrate
        if (settings) {
            convertLegacyTitleToDelta(settings, getQuillOpAttributes(settings));
        }

        const visual = Visual.getInstance(settings);

        /** Settings are null when we are on ChartChooserScreen, Only after choosing the chart should we show the data editor **/
        if (settings === null) {
            Visual.isFirstLoad = true;
        }

        visual.update(settings).then(() => {
            flagHandler
                .init({
                    api: process.env.FLAGSMITH_HOST,
                    defaultFlags: fsm.flags,
                    environment: process.env.MODE === "development" ? process.env.FLAGSMITH_DEV : process.env.FLAGSMITH_PROD,
                    preventFetch: process.env.FLAGSMITH_LOCAL_ONLY === "true",
                    identity: process.env.FLAGSMITH_USER ?? null,
                })
                .then(() => {
                    // currently not needeed, no active flags
                    // const visual = Visual.getInstance(settings);
                    // visual.update(settings);
                });
        });
    }
});

function appendCommentBoxNodes(hostWrapperElement: HTMLElement): void {
    const commentBoxResizeLineElement = document.createElement("div");
    commentBoxResizeLineElement.classList.add(COMMENT_BOX_RESIZE_LINE);
    hostWrapperElement.append(commentBoxResizeLineElement);

    const infoBoxElement = document.createElement("aside");
    infoBoxElement.classList.add(INFO_BOX_CONTAINER);
    hostWrapperElement.append(infoBoxElement);
}

export class Visual {
    private chartsInstance: Charts;
    // each charts update() must finish before the next one starts - so we queue them here
    private chartsUpdateChain: Promise<void> = Promise.resolve();
    private readonly visualInstanceAnnotationsAdapter: VisualInstanceAnnotationsAdapter;
    private readonly annotationsService: AnnotationsService;
    private readonly annotationLayerSubscriber: AnnotationLayerStoreSubscriber;
    private messageBarService: MessageBarService;

    getSettings(): ChartSettings {
        return Visual.settings;
    }

    private addInBodyEl: HTMLBodyElement;
    private chartsContainer: HTMLDivElement;
    public static element: HTMLElement; //todo: remove this?
    public welcomeHeader: HTMLElement;

    private static instance: Visual;

    public static getInstance(settings?: ChartSettings) {
        if (!Visual.instance) {
            Visual.instance = new Visual(settings);
        }
        return Visual.instance;
    }

    public dataHelper: DataHelper;
    public static dataView: DataView;

    public static settings: ChartSettings; //todo: remove these static properties, use ones from instance
    static viewModel: ViewModel;

    private viewModel: ViewModel;

    public showCompanyStyle = false;

    static formatterMap: Map<string, valueFormatter.IValueFormatter>;

    private isLicenseFetchingDone = false;

    static tableDataEditorSwitcher: DataTableEditorSwitcher;
    static isFirstLoad: boolean = false;

    static visualViewPort: Viewport;
    private globalToolbar: GlobalToolbar | GlobalToolbarOld;

    /** The Data fields entry of the old toolbar; the toolbar stays while its pane is open */
    private fieldsSwitcher: FieldsChartsSwitcher | undefined;
    static isFirstTimeInsert: boolean;

    private currentOrganizationStyle: OrganizationStyleData;
    public availableOrganizationStyles: OrganizationStyleData[];

    public getOrganizationStyleSettings(): OrganizationStyleData {
        return this.currentOrganizationStyle;
    }

    public removeOrganizationStyle() {
        this.currentOrganizationStyle = null;
    }

    public async getOrganizationStyle(organizationId: string): Promise<OrganizationStyleSettings> {
        try {
            const getOrganizationStyleUrl = process.env.LICENSING_SERVER_URL + `/add-in/organization-styles/${organizationId}/default`;
            const response = await axios.get(getOrganizationStyleUrl);
            const styleSettings = response.data.styleJSON;

            return styleSettings;
        } catch (e) {
            console.error(e);
        }

        return null;
    }

    public applyDefaultOrganizationStyle(styleId: number) {
        if (Visual.isFirstLoad) {
            this.applyOrganizationStyleById(styleId);
        }
    }

    public applyOrganizationStyleById(styleId: number) {
        getOrganizationStyleById(styleId).then((orgSettings) => {
            if (!orgSettings) {
                return;
            }
            this.currentOrganizationStyle = orgSettings;
            if (Visual.settings) {
                Visual.settings.setOrganizationStyleSettings(this.currentOrganizationStyle);
                this.update(Visual.settings);
            }
        });
    }

    public loadOrganizationStyles(organizationId: string, userId: string) {
        if (!organizationId) {
            return;
        }
        getAvailableOrganizationStyles(organizationId, userId).then((availableStyles) => {
            this.availableOrganizationStyles = availableStyles;
            availableStyles?.forEach((styleData) => {
                if (styleData.isDefault) {
                    this.applyDefaultOrganizationStyle(styleData.id);
                }
            });
        });
    }

    constructor(settings?: ChartSettings) {
        Visual.isFirstTimeInsert = !Office.context.document.settings.get(CHART_SETTINGS_NAME);
        this.dataHelper = new DataHelper({
            currentDataView: () => Visual.dataView,
            onDataChanged: () => void this.update(Visual.settings),
        });

        this.handleSignIn();

        initChartChooserStore();

        if (Office.context.host === Office.HostType.PowerPoint) {
            mountChartChooser();
        }
        initTableDataEditorStore({ visualType: VisualType.CHARTS });
        if (Office.context.host === Office.HostType.PowerPoint) {
            initExcelDataLinkingStore();
        }
        // Read initial table data from Office storage
        const tableData = getOfficeStorageTableData();
        Visual.dataView = prepareTableData(tableData);

        // Local storage event listener
        subscribeToXSpreadSheetData(async (tableData: DataView) => {
            if (
                Office.context.host === Office.HostType.PowerPoint
                && Office.context.document.mode !== Office.DocumentMode.ReadOnly
                && Visual.isFirstLoad
            ) {
                setTimeout(() => Visual.tableDataEditorSwitcher.open(), 100);
            }

            const dataView = prepareTableData(tableData);

            setOfficeStorageTableData(dataView);
            Visual.dataView = dataView;
            if (Office.context.host === Office.HostType.Excel) {
                await this.dataHelper.writeDataViewToWorksheet();
            }
            this.update(Visual.settings);
        });

        subscribeToTableDataEditorData(() => {
            this.update(Visual.settings);
        });

        this.addInBodyEl = document.getElementsByTagName("body")[0];
        Visual.element = this.addInBodyEl;
        this.welcomeHeader = document.getElementById("welcome-header");
        this.welcomeHeader.focus(); // focusing this element enables inset from selected range in Excel

        Visual.visualViewPort = this.getViewport(this.addInBodyEl);

        // chartsInstance container
        this.chartsContainer = document.createElement("div");
        this.chartsContainer.className = "office-charts-container";
        this.addInBodyEl.appendChild(this.chartsContainer);
        this.messageBarService = new MessageBarService(this.addInBodyEl);
        this.visualInstanceAnnotationsAdapter = new VisualInstanceAnnotationsAdapter(
            (annotationCagrArrows) => Visual.settings.persistCagrArrows(annotationCagrArrows),
            { getCagrArrows: () => Visual.settings?.cagrArrows ?? [] },
            createAnnotationModeContextProvider(() => VisualViewMode.Edit)
        );
        const adaptersMap: Map<AnnotationAdapterSource, AnyAnnotationsAdapter> = new Map();
        adaptersMap.set(AnnotationAdapterSource.VisualInstance, this.visualInstanceAnnotationsAdapter);
        this.annotationsService = new AnnotationsService(adaptersMap, AnnotationAdapterSource.VisualInstance);

        annotationLayerStore.setAnnotationsService(this.annotationsService);
        this.annotationLayerSubscriber = createAnnotationLayerStoreSubscriber(
            () => this.constructViewModelAndVisualUpdate(Visual.settings),
            openURL
        );
        annotationLayerStore.subscribe(this.annotationLayerSubscriber);

        this.chartsInstance = createChartsInstance(
            this.chartsContainer,
            new SelectionService(new OfficeSelectionService(Office.context.host === Office.HostType.Excel)),
            appendCommentBoxNodes
        );

        // this.chartsInstance.setTooltipProvider(...);

        Visual.formatterMap = new Map<string, valueFormatter.IValueFormatter>();

        // Add resize handler
        window.onresize = () => {
            Visual.visualViewPort = this.getViewport(this.addInBodyEl);
            this.constructViewModelAndVisualUpdate(Visual.settings);
        };

        d3.select(document.body).on(MOUSELEAVE, () => {
            // check if ppt spreadsheed is open, should not be hidden on mouse leave
            const isDataEditorOpen =
                Office.context.host === Office.HostType.PowerPoint
                && Visual.tableDataEditorSwitcher
                && document.querySelector("." + Visual.tableDataEditorSwitcher.getStyleClassName()).classList.contains("active");
            // with the Fields list on, the Data fields pane is a workspace, not a menu: it stays until the user closes it
            const isFieldsPaneOpen = this.fieldsSwitcher?.getSwitcherContainer()?.classList.contains("active") ?? false;
            const keepsPaneOpen = flagHandler.has(FIELDS_LIST_FLAG) && isFieldsPaneOpen;
            if (!isDataEditorOpen && !keepsPaneOpen) {
                this.globalToolbar?.hide();
            }
        });

        d3.select(document.body).on(MOUSEENTER, () => {
            this.globalToolbar?.show();
        });

        this.initToolbar(settings);

        if (Office.context.host === Office.HostType.Excel && !settings) {
            this.setFocus();
        }

        this.setUpCSSVariables();
        this.addContextMenuDiv();
    }

    // todo: move this context-menu element from apps into charts package
    private addContextMenuDiv() {
        const context = document.createElement(DIV);
        context.className = `${CONTEXT_MENU_DIV} ${CONTEXT_MENU_DIV}-theme`;
        context.addEventListener(CONTEXT_MENU, (e) => e.preventDefault());
        this.chartsContainer.appendChild(context);
        context.onmouseleave = () => {
            const contextMenuDisplay = d3.select("." + CONTEXT_MENU_DIV).style(DISPLAY);
            if (contextMenuDisplay === BLOCK) {
                d3.select("." + CONTEXT_MENU_DIV).style(DISPLAY, NONE);
            }
        };
    }

    private setUpCSSVariables(): void {
        document.documentElement.style.setProperty("--default-font-size", `${DEFAULT_FONT_SIZE}${FONT_SIZE_UNIT}`);
    }

    private getViewport(el: HTMLElement): Viewport {
        return {
            width: el.clientWidth || el.parentElement?.clientWidth,
            height: el.clientHeight || el.parentElement?.clientHeight,
        };
    }

    private setFocus() {
        // her we set focus to some element to deselect the add-in and enable "insert from range" functionality in Excel
        const el = document.createElement("input");
        this.addInBodyEl.appendChild(el);
        this.addInBodyEl.blur();
        el.focus();
        el.hidden = true;
    }

    // reads Excel data and updates the add-in
    public async update(updateSettings?: ChartSettings): Promise<void> {
        if (process.env.DEBUG_LOG === "true") {
            console.debug("updating...", updateSettings, performance.now());
        }

        const dataView = await this.dataHelper.getDataView();
        Visual.dataView = dataView;

        this.constructViewModelAndVisualUpdate(updateSettings);
    }

    public constructViewModelAndVisualUpdate(updateSettings?: ChartSettings): void {
        const locale = Office.context.contentLanguage || "en-US";

        if (Visual.dataView && !Visual.dataView.errorMessage) {
            this.viewModel = viewModelFactory.getViewModel(Visual.dataView, this.showCompanyStyle, locale, updateSettings);
            this.viewModel.settings = new Proxy(this.viewModel.settings, {
                set: (target: ChartSettings, p: string, value: any): boolean => {
                    let oldValue = target[p];
                    if (oldValue !== value) {
                        target[p] = value;
                        if (p === "chartType") {
                            analyticsHandler.report(`edit/${p}/${ChartType[value]}`);
                        } else if (!ignoredEditEvents.includes(p)) {
                            analyticsHandler.report(`edit/${p}`);
                        }
                    }
                    return true;
                },
            });
            Visual.viewModel = this.viewModel;
            Visual.settings = this.viewModel.settings;
        }

        this.chartsUpdate();
    }

    private chartsUpdate() {
        if (!this.chartsInstance) {
            this.chartsInstance = createChartsInstance(
                this.chartsContainer,
                new SelectionService(new OfficeSelectionService(Office.context.host === Office.HostType.Excel)),
                appendCommentBoxNodes
            );
        }

        if (Visual.dataView.errorMessage) {
            this.chartsContainer.style.display = "none";
            this.showErrorMessage(Visual.dataView.errorMessage);
            return;
        }

        this.chartsContainer.style.display = null;
        this.welcomeHeader.style.display = "none";
        if (this.viewModel?.chartData?.length > 0) {
            // ??
            const viewModel = this.viewModel;
            const settings = Visual.settings;

            this.viewModel.isMultiples = this.viewModel.chartData.length > 1;

            // The renderer gets the chart type it can draw; the live settings keep the user's choice (see getRenderChartType).
            const renderChartType = settings.getRenderChartType(viewModel.isMultiples);
            const chartsSettings = renderChartType === settings.chartType ? settings : { ...settings, chartType: renderChartType };
            this.chartsInstance.setSettings(chartsSettings, ChartsViewMode.Edit, this.getLocale(), settings.scenarioOptions);
            //todo? check: ChartsVisual.settings =/merge this.chartsInstance.setSettings.getSettings()

            d3.select("#StackedChartSwitcher").style(DISPLAY, this.viewModel.isMultiples ? BLOCK : NONE);

            if (
                viewModel.isMultiples
                && !viewModel.is2dMultiples
                && ((!settings.stackedChart && settings.showTopNChartsOptions !== ShowTopNChartsOptions.Off)
                    || (settings.stackedChart && settings.showTopNStackedOptions !== ShowTopNChartsOptions.Off))
            ) {
                this.viewModel = viewModelHelpers.replaceSmallerChartsWithOthers(this.viewModel, this.viewModel.settings);
            }

            if (
                !settings.shouldPlotStackedChart(viewModel.isMultiples)
                && settings.hasAxisBreak
                && (renderChartType === ChartType.Waterfall || renderChartType === ChartType.Area || renderChartType === ChartType.Line)
            ) {
                viewModelHelpers.adjustViewModelForAxisBreak(viewModel, settings.axisBreakPercent);
            }

            if (settings.isChartDataToBeSorted(viewModel)) {
                const chartSortType = settings.stackedChart ? settings.stackedChartSort : settings.multiplesSort;
                if (chartSortType === Sort.Ascending) {
                    sortAscending(viewModel.chartData, (item) => item.max - item.min);
                } else if (chartSortType === Sort.Descending) {
                    sortDescending(viewModel.chartData, (item) => item.max - item.min);
                }
            }

            //? this.chartsInstance.ui.setChartsContainerElementWidthHeight(options.viewport.width, options.viewport.height);
            //? this.chartsInstance.ui.setLicensingHandlers(initializeLicensingHandlers());
            const interactionHandlers = initializeInteractionHandlers(this);
            this.chartsInstance.setInteractionHandlers(interactionHandlers);

            this.chartsUpdateChain = this.chartsUpdateChain
                .then(() => {
                    this.visualInstanceAnnotationsAdapter.setCachedAnnotations({ getCagrArrows: () => settings.cagrArrows });
                    prepareAnnotationLayer(
                        this.addInBodyEl,
                        ".office-charts-container",
                        viewModel.chartData,
                        this.getEnabledAnnotationActions(),
                        this.messageBarService,
                        () => settings.getAnnotationLayerSettings(viewModel)
                    );
                    this.chartsContainer.style.border = annotationLayerStore.isInAnnotationMode ? "1px solid #0070C0" : NONE;
                    this.chartsContainer.style.boxSizing = "border-box";
                    return this.chartsInstance.update(
                        {
                            allowInteractiveCommentBox: true,
                            allowMenus: true,
                            clientEventInteractivityAllowed: true,
                            slideSwitchingAllowed: !annotationLayerStore.isInAnnotationMode,
                            pbiFormatEnabled: false,
                        },
                        viewModel
                    );
                })
                .catch((error) => {
                    // charts swallows layout.plot errors internally (ZEN-3323) — anything surfacing here is serious
                    debugLogger.error("charts update failed:", error);
                });

            const infoBox = new InfoBoxHandler(
                settings,
                `.${INFO_BOX_CONTAINER}`,
                `.${COMMENT_BOX_RESIZE_LINE}`,
                `.${ZEBRABI_CHART_SVG_CONTAINER}`
            );
            infoBox.clearCommentBox();
            if (settings.showCommentBox) {
                infoBox.drawCommentBox(this.viewModel.chartData, 0, 0, viewModel.isSingleSeriesViewModel);
                const commentBoxResizeLine = infoBox.clearCommentsBoxDragLine();
                infoBox.setCommentBoxPlacement(commentBoxResizeLine.parentElement);
                infoBox.drawDragLine(Visual.visualViewPort.width, Visual.visualViewPort.height);
            }

            this.globalToolbar?.render(settings.stackedChart);
            this.checkWatermark();
            // add current settings to persist queue...
            Visual.settings?.persist();
        }
    }

    private showErrorMessage(errorMsg: string) {
        this.welcomeHeader.style.display = null;
        d3.select(this.welcomeHeader).classed("error-message", true);
        d3.select(this.welcomeHeader).html(errorMsg);

        const chooserLink = this.welcomeHeader.querySelector<HTMLAnchorElement>(`#${SHOW_CHOOSER_LINK_ID}`);
        if (chooserLink) {
            chooserLink.addEventListener(CLICK, () => mountChartChooser(true));
        }

        const kbLink = <HTMLAnchorElement>this.welcomeHeader.querySelector("#error-msg-kb-url");
        if (kbLink) {
            kbLink.addEventListener(CLICK, (e) => {
                e.preventDefault();
                e.stopPropagation();
                openURL(kbLink.href);
            });
        }

        const templateLink = <HTMLAnchorElement>this.welcomeHeader.querySelector("#error-msg-template-url");
        if (templateLink) {
            templateLink.addEventListener(CLICK, (e) => {
                e.preventDefault();
                e.stopPropagation();
                openURL(templateLink.href);
            });
        }
    }

    private initToolbar(settings: ChartSettings) {
        this.initGlobalToolbarOld(settings);
    }

    public initGlobalToolbar(settings?: ChartSettings) {
        const globalToolbar = new GlobalToolbar(this.addInBodyEl);
        globalToolbar.add(new AboutSwitcher());

        const chartSettingsSwitcher = new ChartSettingsSwitcher();

        // Observer type determines what object gets the returned data if you set it in the switcher notify call
        const visualObserver: ChartSettingsSwitcherObserver = new (class extends ChartSettingsSwitcherObserver {})();
        visualObserver.update = (formData: Map<string, any>) => {
            for (let [key, value] of formData) {
                if (isNaN(Number(value))) {
                    continue;
                }

                formData.set(key, Number(value));
            }

            this.updateSettingsFromForm(formData);
            this.constructViewModelAndVisualUpdate(Visual.settings);
        };

        chartSettingsSwitcher.attach(visualObserver); // start listening for changes
        globalToolbar.add(chartSettingsSwitcher);
        globalToolbar.render();

        this.globalToolbar = globalToolbar;
    }

    public initGlobalToolbarOld(settings?: ChartSettings) {
        const globalToolbar = new GlobalToolbarOld(this.addInBodyEl);

        const fieldsSwitcher = new FieldsChartsSwitcher();
        this.fieldsSwitcher = fieldsSwitcher;
        const fieldsSwitcherObserver = new (class extends FieldsChartsSwitcherObserver {})();
        fieldsSwitcherObserver.update = (formData: Map<string, any>) => {
            this.updateSettingsFromForm(formData);
            this.constructViewModelAndVisualUpdate(Visual.settings);
        };
        fieldsSwitcher.attach(fieldsSwitcherObserver);

        const settingsSwitcher = new SettingsChartsSwitcher();
        const settingsSwitcherObserver = new (class extends SettingsChartsSwitcherObserver {})();
        settingsSwitcherObserver.update = (formData: Map<string, any>) => {
            this.updateSettingsFromForm(formData);
            this.constructViewModelAndVisualUpdate(Visual.settings);
        };
        settingsSwitcher.attach(settingsSwitcherObserver);

        if (Office.context.host === Office.HostType.PowerPoint) {
            // Observer type determines what object gets the returned data if you set it in the switcher notify call
            const dataTableEditorObserver = new (class extends TableDataEditorObserver {})();
            dataTableEditorObserver.update = (): void => {
                this.update(Visual.settings);
            };

            const tableDataEditorSwitcher = new DataTableEditorSwitcher();
            tableDataEditorSwitcher.attach(dataTableEditorObserver);
            globalToolbar.add(tableDataEditorSwitcher);
            Visual.tableDataEditorSwitcher = tableDataEditorSwitcher;
        }

        const stackedChartSwitcher = new StackedChartSwitcher();
        const stackedChartObserver = new StackedChartObserver();
        stackedChartSwitcher.attach(stackedChartObserver);

        const annotationModeSwitcher = new AnnotationModeSwitcher();
        annotationModeSwitcher.attach(new AnnotationModeObserver());

        const accountSwitcher = new AccountSwitcher();

        globalToolbar.add(stackedChartSwitcher);
        globalToolbar.add(fieldsSwitcher);
        globalToolbar.add(settingsSwitcher);
        globalToolbar.add(annotationModeSwitcher);
        globalToolbar.add(accountSwitcher);
        globalToolbar.add(new AboutSwitcherOld(settings));

        globalToolbar.render(settings?.stackedChart);
        this.globalToolbar = globalToolbar;
    }

    /**
     * The floating menu's actions. Mirrors the Power BI charts visual minus comments and SharePoint writeback, which
     * Office does not support.
     */
    private getEnabledAnnotationActions(): EnabledAnnotationActions {
        return [
            { [AnnotationAction.AddCagrArrow]: () => canAddCagrArrow(this.viewModel, Visual.settings) },
            AnnotationAction.ReadMore,
            AnnotationAction.Close,
        ];
    }

    private updateSettingsFromForm(formData: Map<string, any>) {
        for (const [key, value] of formData.entries()) {
            const splitKey = key.split(".");
            let setting = Visual.settings;

            if (splitKey.length > 1) {
                while (splitKey.length - 1 && (setting = setting[splitKey.shift()]));
            }

            setting[splitKey.shift()] = value;
        }

        Visual.settings.persist();
    }

    private async handleSignIn() {
        const signInObserver = new ActionObserver();
        signInObserver.update = async (data: string, action: string) => {
            if (action === ActionType.SignInSuccess) {
                return await this.onSignIn();
            }

            if (action === ActionType.SignOut) {
                return await this.onSignOut();
            }
        };
        signInService.attach(signInObserver);

        try {
            await signInService.silentSignIn();
        } catch (error) {
            if (process.env.DEBUG_LOG === "true") {
                console.debug(error.name, error);
            }

            if (!licensing.getOwnerUser()) {
                signInService.renderSignIn();
            }
        }
    }

    private refreshGlobalToolbar() {
        this.constructViewModelAndVisualUpdate(Visual.settings);
        this.globalToolbar?.render(Visual.settings?.stackedChart);
    }

    private shouldRefreshPPTLinkedData(): boolean {
        if (Office.context.host !== Office.HostType.PowerPoint) {
            return false;
        }

        const dataLinkingStore = ExcelDataLinkingStore.get();
        if (!dataLinkingStore?.loaded?.autoRefresh) {
            return false;
        }

        const timeSinceLastRefresh = new Date().getTime() - (dataLinkingStore?.loaded?.dataRefreshTimestamp?.getTime() ?? 0);
        if (timeSinceLastRefresh < 5000) {
            // arbitrary 5s threshold
            return false;
        }

        return true;
    }

    private async refreshPPTLinkedData() {
        const loadRangeAndSetData = async (accessToken: string) => {
            const loaded = ExcelDataLinkingStore.get().loaded;
            const { driveId, fileId, worksheetId, workbookId, range } = loaded;

            const browser = SharepointBrowserAPI.createInstance(accessToken);
            const response = workbookId
                ? await browser.getTableRange(driveId, fileId, workbookId)
                : await browser.getRange(driveId, fileId, worksheetId, range);

            if (response?.message) {
                console.error("Error loading range data. Graph API error: ", response.message);
                // set error message in store
                ExcelDataLinkingStore.set({
                    loaded: {
                        ...loaded,
                        errorMessage: response.message,
                    },
                });

                // open datagrid on error
                setTimeout(() => Visual.tableDataEditorSwitcher.open(), 100);

                return;
            }

            const rows = transformSharePointResponse(response.values);
            // update refresh data timestamp
            ExcelDataLinkingStore.set({
                loaded: {
                    ...loaded,
                    errorMessage: null,
                    dataRefreshTimestamp: new Date(),
                },
            });

            dataLinkingEventBus.dispatch("range-selected", rows);
        };

        this.showDataLoadingBanner();

        await signInService.authenticateWithFilePermissions(licensing.getCurrentUser()?.organizationId);
        await loadRangeAndSetData(signInService.getSharepointToken());

        this.removeDataLoadingBanner();
    }

    private showDataLoadingBanner() {
        const dataLoadingBanner = document.createElement("div");
        dataLoadingBanner.className = "data-refreshing-banner";
        const icon = new IconButton(IconName.ArrowSync, false);
        icon.appendTo(dataLoadingBanner);

        dataLoadingBanner.appendChild(document.createTextNode(" Loading data from Excel..."));
        this.addInBodyEl.appendChild(dataLoadingBanner);
    }

    private removeDataLoadingBanner() {
        const dataLoadingBanner = document.querySelector(".data-refreshing-banner");
        dataLoadingBanner?.remove();
    }

    private async onSignIn() {
        await this.checkLicensing();

        const organizationId = licensing.getCurrentUser()?.organizationId;
        const userId = licensing.getCurrentUser()?.userId;
        this.loadOrganizationStyles(organizationId, userId);

        this.logSignInAnalytics();

        if (this.shouldRefreshPPTLinkedData()) {
            this.refreshPPTLinkedData();
        }

        this.refreshGlobalToolbar();
    }

    private async onSignOut() {
        this.globalToolbar.hide();
        licensing.clearWatermark();
    }

    private checkWatermark() {
        if (!this.isLicenseFetchingDone) {
            return;
        }

        const ownerUser = licensing.getOwnerUser();
        const currentUser = licensing.getCurrentUser();

        if (!currentUser) {
            licensing.renderLicensingUnavailableWatermark();
            return;
        }

        // at the moment when visual is newly added it has no owner
        if (!ownerUser && currentUser && !currentUser.getLicense().hasLicense) {
            licensing.renderFreeLicenseWatermark();
            return;
        }

        if (licensing.freeViewerMode()) {
            licensing.renderFreeLicenseWatermark();
            return;
        }

        if (!signInService.isSignedIn() && ownerUser && !ownerUser.getLicense()?.hasLicense) {
            licensing.renderFreeLicenseWatermark();
            return;
        }

        // no watermark is required, clear any existing watermark
        licensing.clearWatermark();
    }

    private async checkLicensing() {
        try {
            await Promise.race([
                licensing.fetchUsers(),
                delay(10000).then(() => {
                    throw new Error("Fetching licensing data timed out");
                }),
            ]);

            const currentUser = licensing.getCurrentUser();
            const ownerUser = licensing.getOwnerUser();

            if (!ownerUser && !currentUser?.getLicense()?.hasLicense) {
                licensing.renderNoLicenseOverlay();
                return;
            }

            if (
                !currentUser?.getLicense()?.hasLicense
                && currentUser?.isSameUserAs(ownerUser)
                && (!ownerUser?.lastDisplayedOverlay
                    || ownerUser?.lastDisplayedOverlay.getTime() < new Date().getTime() - 30 * 24 * 3600 * 1000)
            ) {
                if (ownerUser) {
                    ownerUser.lastDisplayedOverlay = new Date();
                }
                await OwnerUserInfo.setOwnerInfo(ownerUser);

                licensing.renderNoLicenseOverlay();
                return;
            }
        } catch (error) {
            if (error instanceof Error && error.message === "Fetching licensing data timed out") {
                console.error("Licensing service not available:", error.message);
            }
        } finally {
            this.isLicenseFetchingDone = true;
        }
    }

    private logSignInAnalytics() {
        const currentUser = licensing.getCurrentUser();
        if (!currentUser) {
            return;
        }

        Visual.isFirstTimeInsert = initializeAnalyticsReporting("charts", analyticsHandler, Visual.isFirstTimeInsert, this.dataHelper);
        analyticsHandler.identify({
            userId: currentUser?.userId,
            organizationId: currentUser?.organizationId,
            plan: licensing.getPlanString(),
        });
        initializeAnalyticsOptOut(analyticsHandler, currentUser, this.globalToolbar).finally(() => {
            // We wait for opt out to be correctly set before extracting existing analytics from current file
            extractExistingAnalyticsId(analyticsHandler);
        });
    }

    private getLocale(): string {
        return Office.context.contentLanguage || "en-US";
    }
}
